/**
 * Normalization utilities shared by the web app, the API Worker and the Queue
 * consumer.
 *
 * There is exactly one implementation on purpose: `full_name_search` and
 * `phone_normalized` are computed by the consumer before insert
 * (docs/00_TECHNICAL_ARCHITECTURE_v1.2.md §20) and by the admin API when
 * building a search query. If the two drifted apart, admin search would
 * silently stop matching rows that are actually in D1.
 *
 * Rules come from docs/01_DATABASE_SCHEMA.md §8.
 */

const WHITESPACE_RUN = /\s+/g;
const COMBINING_MARKS = /\p{M}+/gu;

/** Trim and collapse internal whitespace runs to a single space. */
export function collapseWhitespace(input: string): string {
  return input.trim().replace(WHITESPACE_RUN, ' ');
}

/**
 * Lowercase, strip Vietnamese diacritics and fold `đ` to `d`.
 *
 * `đ`/`Đ` (U+0111/U+0110) are standalone letters rather than a base letter
 * plus a combining mark, so Unicode decomposition alone does not remove the
 * stroke — it needs the explicit replacement below.
 */
export function normalizeSearchText(input: string): string {
  return collapseWhitespace(input)
    .toLowerCase()
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(/đ/g, 'd');
}

/** Display value stored in `checkins.full_name`. */
export function normalizeFullName(input: string): string {
  return collapseWhitespace(input);
}

/** Search value stored in `checkins.full_name_search`. */
export function normalizeFullNameSearch(input: string): string {
  return normalizeSearchText(input);
}

/** Display value stored in `checkins.phone`. */
export function normalizePhone(input: string): string {
  return collapseWhitespace(input);
}

/**
 * Search value stored in `checkins.phone_normalized`.
 *
 * Keeps digits, and keeps a `+` only when it is the leading character. No
 * country inference in Phase 1 — `0901234567` and `+84901234567` stay
 * distinct values.
 */
export function normalizePhoneSearch(input: string): string {
  const trimmed = input.trim();
  const digits = trimmed.replace(/\D/g, '');
  return trimmed.startsWith('+') ? `+${digits}` : digits;
}

const PHONE_SEPARATORS = /[\s().-]/g;

/**
 * A Vietnamese phone number as it should be stored: digits, leading zero.
 *
 * Applied before the number is validated, not instead of validating it. People
 * at a check-in desk type `090 123 4567`, `090-123-4567` and `+84901234567`,
 * and all three mean one number. Rejecting the spacing would cost a participant
 * a second attempt at the door for nothing.
 *
 * `84` is rewritten to `0` only when the value does not already start with a
 * zero, so `0845678901` (a real Vietnamobile prefix) is left alone.
 */
export function normalizeVietnamPhone(input: string): string {
  const compact = input.trim().replace(PHONE_SEPARATORS, '').replace(/^\+/, '');
  if (compact.startsWith('84') && !compact.startsWith('0')) {
    return `0${compact.slice(2)}`;
  }
  return compact;
}

/** Value stored in `checkins.email`. */
export function normalizeEmail(input: string): string {
  return input.trim().toLowerCase();
}

/** Normalized token used to match a location by code, name or alias. */
export function normalizeAlias(input: string): string {
  return normalizeSearchText(input);
}
